import * as React from "react";
import { Button, Modal } from "semantic-ui-react";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";

import { CustomerUserCaseTypeOrgStructures } from "@teamprevent/common/customerUser/models";

import { SAVE_CUSTOMERUSER } from "../actiontypes";
import { CUSTOMER_ROLES, Role } from "../../../auth/model";

import { EditUnconnected, EditProps, DispatchProps } from "./Edit";

interface State {
    initialRoles: string[] | undefined;
    currentRoles: string[] | undefined;
    emailAddress: string | undefined;
    caseTypeOrgStructures: CustomerUserCaseTypeOrgStructures[];
}

export class EditModal extends React.Component<EditProps & DispatchProps, State> {
    constructor(props: EditProps & DispatchProps) {
        super(props);
        this.state = {
            initialRoles: undefined,
            currentRoles: undefined,
            emailAddress: undefined,
            caseTypeOrgStructures: []
        };
        this.onClose = this.onClose.bind(this);
        this.onRolesChanged = this.onRolesChanged.bind(this);
        this.getSaveButton = this.getSaveButton.bind(this);
        this.onEmailAddressChanged = this.onEmailAddressChanged.bind(this);
        this.onCaseTypeOrgStructuresChanges = this.onCaseTypeOrgStructuresChanges.bind(this);
    }

    private editForm: EditUnconnected;

    public componentDidMount() {
        this.setState({
            initialRoles: this.props.model.roles,
            currentRoles: this.props.model.roles,
            emailAddress: this.props.model.emailAddress,
            caseTypeOrgStructures: this.props.model.caseTypeOrgStructures
        });
    }

    public componentDidUpdate() {
        const hasStateBeenCleared = this.state.initialRoles === undefined && this.state.currentRoles === undefined;
        const customerUserLoaded = !!this.props.model?.roles;
        if (hasStateBeenCleared && customerUserLoaded) {
            this.setState({
                initialRoles: this.props.model.roles,
                currentRoles: this.props.model.roles,
                emailAddress: this.props.model.emailAddress,
                caseTypeOrgStructures: this.props.model.caseTypeOrgStructures
            });
        }
    }

    public render() {
        const model = {
            ...this.props.model,
            roles: this.state.currentRoles ?? this.props.model?.roles,
            emailAddress: this.state.emailAddress ?? this.props.model?.emailAddress,
            caseTypeOrgStructures: this.state.caseTypeOrgStructures ?? this.props.model?.caseTypeOrgStructures
        };
        return (
            <Modal size="small" open={this.props.open} onClose={this.props.close}>
                <Modal.Header>Edit Customer User</Modal.Header>
                <Modal.Content>
                    <EditUnconnected
                        open={this.props.open}
                        save={this.props.save}
                        model={model}
                        customer={this.props.customer}
                        ref={instance => this.editForm = instance}
                        onRolesChanged={this.onRolesChanged}
                        currentUser={this.props.currentUser}
                        allowedAssessmentAndWellBeingFeatures={this.props.allowedAssessmentAndWellBeingFeatures}
                        orgStructures={this.props.orgStructures}
                        onEmailAddressChanged={this.onEmailAddressChanged}
                        onCaseTypeOrgStructuresChangesProp={this.onCaseTypeOrgStructuresChanges}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={this.props.close}>Cancel</Button>
                    {this.getSaveButton()}
                </Modal.Actions>
            </Modal>
        );
    }

    private onClose() {
        this.setState({
            initialRoles: undefined,
            currentRoles: undefined,
            emailAddress: undefined,
            caseTypeOrgStructures: []
        }, () => this.props.close());
    }

    private onEmailAddressChanged(emailAddress: string) {
        this.setState({
            ...this.state,
            emailAddress
        });
    }

    private onRolesChanged(roles: string[]) {
        this.setState({
            currentRoles: roles
        });
    }

    private onCaseTypeOrgStructuresChanges(caseTypeOrgStructures: CustomerUserCaseTypeOrgStructures[]) {
        this.setState({
            ...this.state,
            caseTypeOrgStructures
        });
    }

    private shouldShowViewAllAccessWarning(): boolean {
        if (this.state.caseTypeOrgStructures) {
            const selectedValues = this.state.caseTypeOrgStructures.flatMap((caseTypeOrgStructure) => {
                return caseTypeOrgStructure.orgStructures.map((orgStruct) => {
                    return orgStruct.id;
                });
            });
            return selectedValues.length > 0;
        }

        return false;
    }

    private shouldShowRoleChangeWarning(): boolean {
        const hasRoleBeenRemoved = (role: Role) => this.state.initialRoles?.indexOf(role.name) !== -1 && this.state.currentRoles?.indexOf(role.name) === -1;
        const hasRoleAffectingAssignmentToCasesBeenRemoved =
            hasRoleBeenRemoved(CUSTOMER_ROLES.manager) ||
            hasRoleBeenRemoved(CUSTOMER_ROLES.recruitmentManager) ||
            hasRoleBeenRemoved(CUSTOMER_ROLES.hr);

        return hasRoleAffectingAssignmentToCasesBeenRemoved;
    }

    private buildPromptText(showViewAllAccessWarning: boolean, showRoleChangeWarning: boolean): string {
        let promptText = "Are you sure you want to edit this user? By editing this user you are:\n";

        if (showRoleChangeWarning)
        {
            // eslint-disable-next-line max-len
            promptText += "\n- Removing them from any cases which they are associated with and are no-longer eligible to be,given their new role assignments.";
        }

        if (showViewAllAccessWarning)
        {
            if (showRoleChangeWarning)
            {
                promptText += "\n";
            }
            // eslint-disable-next-line max-len
            promptText += "\n- Giving them view all access to all information under the selected product options for cases not marked as totally confidential, regardless of employee consent.";
        }

        promptText += "\n\nDo you wish to continue? Type 'EDIT' and click confirm to edit.";
        return promptText;
    }

    private getSaveButton() {
        const showViewAllAccessWarning = this.shouldShowViewAllAccessWarning();
        const showRoleChangeWarning = this.shouldShowRoleChangeWarning();

        if (showViewAllAccessWarning || showRoleChangeWarning)
        {
            return (<ConfirmedButton
                content="Save"
                onClick={() => this.onSubmit()}
                promptText={this.buildPromptText(showViewAllAccessWarning, showRoleChangeWarning)}
                typeToConfirmText="EDIT"
                headingText="Edit Customer User"
                modalClassName="with-line-breaks"
            />);
        }
        return <IsLoadingButton requestNames={SAVE_CUSTOMERUSER} content="Save" onClick={this.onSubmit} />;
    }

    private onSubmit = () => {
        this.editForm.submit();
    };
}
